/**
 * Accès localStorage/sessionStorage qui ne throw JAMAIS.
 *
 * Pourquoi : Sentry remonte des crashs bruts sur les accès storage —
 * `ReferenceError: Can't find variable: localStorage` (contexte sans Web
 * Storage), `TypeError: null is not an object (evaluating
 * 'localStorage.getItem')` (Firefox avec dom.storage désactivé renvoie null),
 * `SecurityError: Access is denied` (iframe tierce, mode privé verrouillé).
 * Chacun de ces cas plantait le flux appelant (interception Alma, persistance
 * checkout) au lieu de dégrader proprement.
 *
 * Comportement : en cas de storage indisponible, on bascule sur un fallback
 * mémoire (perdu au reload, mais le flux courant survit) et on émet UN SEUL
 * warning Sentry par type de storage et par chargement de page, tagué
 * `flow=storage`, pour garder la visibilité sans le bruit.
 */

import * as Sentry from '@sentry/react';

type StorageKind = 'local' | 'session';

const memoryFallback: Record<StorageKind, Map<string, string>> = {
  local: new Map(),
  session: new Map(),
};

const reported: Record<StorageKind, boolean> = { local: false, session: false };

function getStore(kind: StorageKind): Storage | null {
  try {
    // `window.localStorage` peut throw (SecurityError) ou être null/undefined
    // (Firefox dom.storage désactivé, vieux webviews).
    const store = kind === 'local' ? window.localStorage : window.sessionStorage;
    return store ?? null;
  } catch {
    return null;
  }
}

function reportUnavailable(kind: StorageKind, op: string, key: string, error?: unknown): void {
  if (reported[kind]) return;
  reported[kind] = true;
  try {
    Sentry.captureMessage(`storage: ${kind}Storage unavailable, memory fallback in use`, {
      level: 'warning',
      tags: { flow: 'storage', storage_kind: kind },
      extra: { op, key, error: error instanceof Error ? error.message : String(error ?? 'store is null') },
    });
  } catch {
    /* Sentry pas encore initialisé (appel avant Sentry.init) : tant pis. */
  }
}

function makeSafeStorage(kind: StorageKind) {
  return {
    getItem(key: string): string | null {
      const store = getStore(kind);
      if (store) {
        try {
          return store.getItem(key);
        } catch (e) {
          reportUnavailable(kind, 'getItem', key, e);
        }
      } else {
        reportUnavailable(kind, 'getItem', key);
      }
      return memoryFallback[kind].get(key) ?? null;
    },

    setItem(key: string, value: string): void {
      const store = getStore(kind);
      if (store) {
        try {
          store.setItem(key, value);
          return;
        } catch (e) {
          // Quota plein ou mode privé verrouillé : fallback mémoire.
          reportUnavailable(kind, 'setItem', key, e);
        }
      } else {
        reportUnavailable(kind, 'setItem', key);
      }
      memoryFallback[kind].set(key, value);
    },

    removeItem(key: string): void {
      const store = getStore(kind);
      if (store) {
        try {
          store.removeItem(key);
        } catch (e) {
          reportUnavailable(kind, 'removeItem', key, e);
        }
      } else {
        reportUnavailable(kind, 'removeItem', key);
      }
      memoryFallback[kind].delete(key);
    },
  };
}

export const safeLocalStorage = makeSafeStorage('local');
export const safeSessionStorage = makeSafeStorage('session');
